// Referrals workflow (audit #16) — ASKs + RECEIVEDs, plus a manual
// "log a referral ask sent" / "log a referral received" form.
import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Plus, Send, Inbox, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function ReferralsWorkflowView({ currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState({ requests: [], received: [] });
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab]         = React.useState('sent'); // 'sent' | 'received'

  const [askName, setAskName]   = React.useState('');
  const [askPhone, setAskPhone] = React.useState('');
  const [askChannel, setAskChannel] = React.useState('sms');
  const [askCadence, setAskCadence] = React.useState('manual');
  const [askBody, setAskBody]   = React.useState('Hey {{name}} — quick favor: do you know anyone else who might want a quick coverage review? No pressure, just appreciate any intros.');

  const [recName, setRecName]   = React.useState('');
  const [recPhone, setRecPhone] = React.useState('');
  const [recBy, setRecBy]       = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setData(await apiFetch('/api/referrals')); }
    catch (err) { showToast(`Load failed: ${err.message}`); }
    finally { setLoading(false); }
  }, [showToast]);
  React.useEffect(() => { load(); }, [load]);

  const logAsk = async () => {
    if (!askName.trim()) { showToast('Client name required.'); return; }
    try {
      await apiFetch('/api/referrals', {
        method: 'POST',
        body: JSON.stringify({
          action: 'request',
          client_name: askName, client_phone: askPhone,
          channel: askChannel, cadence_step: askCadence,
          message_body: askBody,
        }),
      });
      showToast('Referral ask logged.');
      setAskName(''); setAskPhone('');
      load();
    } catch (err) { showToast(`Log failed: ${err.message}`); }
  };

  const logReceived = async () => {
    if (!recName.trim()) { showToast('Prospect name required.'); return; }
    try {
      await apiFetch('/api/referrals', {
        method: 'POST',
        body: JSON.stringify({
          action: 'receive',
          prospect_name: recName, prospect_phone: recPhone,
          referrer_name: recBy,
        }),
      });
      showToast('Referral received — logged as a new lead in the queue.');
      setRecName(''); setRecPhone(''); setRecBy('');
      load();
    } catch (err) { showToast(`Log failed: ${err.message}`); }
  };

  const updateStatus = async (table, id, status) => {
    try {
      const patch = { action: 'update', table, id, status };
      if (status === 'responded') patch.responded_at = new Date().toISOString();
      if (status === 'closed' || status === 'lost') patch.closed_at = new Date().toISOString();
      await apiFetch('/api/referrals', { method: 'POST', body: JSON.stringify(patch) });
      load();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  };

  const askCount = data.requests.length;
  const responded = data.requests.filter(r => r.status === 'responded' || r.status === 'converted').length;
  const recCount = data.received.length;
  const closed   = data.received.filter(r => r.status === 'closed').length;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-purple-900 text-white rounded-xl p-5">
        <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Referrals · Workflow</div>
        <h1 className="text-xl font-bold mt-1">Your warmest pipeline source</h1>
        <p className="text-slate-300 text-sm mt-2">
          {askCount} asks sent · {responded} responded · {recCount} referrals received · {closed} closed.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Log an ASK */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5"><Send size={11}/> Log a referral ask</div>
          <div className="space-y-2">
            <input value={askName} onChange={e => setAskName(e.target.value)} placeholder="Client you're asking" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input value={askPhone} onChange={e => setAskPhone(e.target.value)} placeholder="Phone (optional)" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select value={askChannel} onChange={e => setAskChannel(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="call">Call</option>
                <option value="manual">Manual</option>
              </select>
              <select value={askCadence} onChange={e => setAskCadence(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                <option value="manual">Manual</option>
                <option value="month_3">3-month touch</option>
                <option value="month_6">6-month touch</option>
                <option value="month_9">9-month touch</option>
                <option value="anniversary">Anniversary</option>
              </select>
            </div>
            <textarea value={askBody} onChange={e => setAskBody(e.target.value)} rows={3} className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 font-mono"/>
            <button onClick={logAsk} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-1.5 rounded-md">Log ask</button>
          </div>
        </div>

        {/* Log a RECEIVED */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5"><Inbox size={11}/> Log a received referral</div>
          <div className="space-y-2">
            <input value={recName} onChange={e => setRecName(e.target.value)} placeholder="Prospect name" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input value={recPhone} onChange={e => setRecPhone(e.target.value)} placeholder="Prospect phone" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input value={recBy} onChange={e => setRecBy(e.target.value)} placeholder="Referred by" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <button onClick={logReceived} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-1.5 rounded-md">Log received</button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="flex gap-1">
        <button onClick={() => setTab('sent')} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${tab === 'sent' ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>Asks Sent ({data.requests.length})</button>
        <button onClick={() => setTab('received')} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${tab === 'received' ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>Received ({data.received.length})</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-4 text-sm text-slate-500">Loading…</div> :
         (tab === 'sent' ? data.requests : data.received).length === 0 ? <div className="p-4 text-sm text-slate-500 italic">No entries yet.</div> :
         <div className="divide-y divide-slate-100">
           {(tab === 'sent' ? data.requests : data.received).slice(0, 50).map(r => (
             <div key={r.id} className="px-4 py-2 flex items-center gap-3">
               <div className="flex-1 min-w-0">
                 <div className="text-sm font-bold text-slate-900">{r.client_name || r.prospect_name}</div>
                 <div className="text-xs text-slate-500">
                   {tab === 'sent'
                     ? `${r.cadence_step} · ${r.channel} · ${new Date(r.sent_at || r.created_at).toLocaleDateString()}`
                     : `${r.referrer_name ? `via ${r.referrer_name} · ` : ''}${new Date(r.created_at).toLocaleDateString()}`}
                 </div>
               </div>
               <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${r.status === 'closed' || r.status === 'converted' || r.status === 'responded' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.status === 'lost' || r.status === 'declined' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>{r.status}</span>
               {tab === 'sent' && r.status === 'sent' && (
                 <button onClick={() => updateStatus('request', r.id, 'responded')} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><CheckCircle size={10}/> Replied</button>
               )}
               {tab === 'received' && r.status === 'new' && (
                 <button onClick={() => updateStatus('received', r.id, 'closed')} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900">Close-Won</button>
               )}
             </div>
           ))}
         </div>}
      </div>
    </div>
  );
}
